import { BreadcrumbItem } from '@ui/Breadcrumb';
import { useDataDepotProduct } from '@util/hooks/useDataDepotProduct';
import { activateProductInfoTab } from '@util/productInfoTabNavigation';
import { useAuth } from 'context/AuthContext';
import { ProductDocument, Variation } from 'models/product';
import { useEffect, useMemo } from 'react';
import PartNumbersTab from './tabs/PartNumberTab';
import ProductDescriptionTab from './tabs/ProductDescriptionTab';
import ProductFitmentTab from './tabs/ProductFitmentTab';
import ProductSpecsTab from './tabs/ProductSpecsTab';
import SizingChartTab from './tabs/SizingChartTab';
import SizingFitTab from './tabs/SizingFitTab';
import { useWarehouse } from './WarehouseProvider';

type ProductInfoTabsProps = {
  product: ProductDocument;
  breadcrumbs: BreadcrumbItem[];
  selectedVariations: Variation[] | null;
  onSelection: (product: ProductDocument) => void;
};

export default function ProductInfoTabs({
  product,
  breadcrumbs,
  selectedVariations,
  onSelection,
}: ProductInfoTabsProps) {
  const { userDoc } = useAuth();
  const { aggregatedFitment } = useWarehouse();

  // A matching data depot product can carry manufacturer sizing charts that the
  // listing itself doesn't have.
  const { dataDepotProduct } = useDataDepotProduct([
    ...(product.mpn ?? []),
    ...(product.sku ? [product.sku] : []),
    ...(product.sources?.map((source) => source.id) ?? []),
  ]);
  const sizeCharts = (dataDepotProduct?.size_charts ?? []).filter(
    (chart) => !!chart.headers?.length && !!chart.rows?.length
  );

  const tabs = [
    {
      id: 'description',
      title: 'Product Details',
      shortTitle: 'Details',
      content: (
        <ProductDescriptionTab
          product={product}
          breadcrumbs={breadcrumbs}
          selectedVariations={selectedVariations}
        />
      ),
    },
    {
      id: 'fitment',
      title: 'Fitment',
      shortTitle: '',
      content: (
        <ProductFitmentTab
          product={product}
          breadcrumbs={breadcrumbs}
          userDoc={userDoc}
          onSelection={onSelection}
        />
      ),
    },
    {
      id: 'part-numbers',
      title: 'Part Numbers',
      shortTitle: '',
      content: <PartNumbersTab product={product} />,
    },
    {
      id: 'sizing-chart',
      title: 'Sizing Chart',
      shortTitle: 'Sizing',
      content: <SizingChartTab charts={sizeCharts} />,
    },
    {
      id: 'sizing-fit',
      title: 'Sizing & Fit',
      shortTitle: '',
      content: <SizingFitTab product={product} />,
    },
    {
      id: 'specs',
      title: 'Specs',
      shortTitle: 'Specs',
      content: (
        <ProductSpecsTab
          product={product}
          selectedVariations={selectedVariations}
        />
      ),
    },
  ] as const;

  // Gear has no bike fitment, but imported gear listings can still carry
  // `fitting_bikes.universal: true`.
  const isGear = product.category === 'Riding Gear';
  const hasFitment =
    !isGear &&
    (!!product.fitting_bikes?.bikes?.length ||
      !!product.fitting_bikes?.universal ||
      !!aggregatedFitment.bikes.length);
  const hasPartNumbers = !!(
    product.variations?.some((v) => v.mpn?.length) || product.mpn?.length
  );
  const hasSizingFit = !!(
    product.category1?.toLowerCase().includes('boots') &&
    product.variations?.some(
      (v) => v.is_number && typeof v.size === 'number' && v.size > 30
    )
  );
  const hasSizeCharts = !!sizeCharts.length;
  const variationSpecs = selectedVariations?.[0]?.specs;
  const specs = variationSpecs ?? product.specs;
  const hasSpecs = !!(specs && Object.keys(specs).length > 0);

  const visibleTabIds = useMemo(() => {
    const ids: string[] = ['description'];
    if (hasFitment) ids.push('fitment');
    if (hasPartNumbers) ids.push('part-numbers');
    if (hasSizeCharts) ids.push('sizing-chart');
    if (hasSizingFit) ids.push('sizing-fit');
    if (hasSpecs) ids.push('specs');
    return ids;
  }, [hasFitment, hasPartNumbers, hasSizeCharts, hasSizingFit, hasSpecs]);

  const visibleTabs = tabs.filter((tab) => visibleTabIds.includes(tab.id));

  useEffect(() => {
    const visibleTabIdSet = new Set(visibleTabIds);

    const selectTabFromHash = () => {
      const tabId = window.location.hash.slice(1);
      if (!tabId || !visibleTabIdSet.has(tabId)) return;
      activateProductInfoTab(tabId);
    };

    selectTabFromHash();
    window.addEventListener('hashchange', selectTabFromHash);
    return () => window.removeEventListener('hashchange', selectTabFromHash);
  }, [visibleTabIds]);

  return (
    <div id="fitment" className="scroll-mt-32 px-4 sm:px-0">
      <div className="m-0 -mx-4 overflow-x-hidden pb-4 sm:mx-0">
        {visibleTabs.map((tab, index) => (
          <input
            key={tab.id}
            type="radio"
            id={`tab-${tab.id}`}
            name="product-tabs"
            defaultChecked={index === 0}
            className="hidden"
          />
        ))}

        <ul className="m-0 flex list-none items-stretch border-b border-l border-zinc-300 p-0">
          {visibleTabs.map((tab, index) => (
            <li key={tab.id} className="relative">
              <label
                htmlFor={`tab-${tab.id}`}
                className={`block w-full flex-shrink-0 flex-grow cursor-pointer whitespace-nowrap bg-zinc-100 px-16 py-4 text-center text-[1.8rem] font-normal text-black transition-all duration-200 hover:bg-zinc-200 ${
                  visibleTabs.length === 1 ? 'border border-zinc-300' : ''
                }`}
              >
                <span className="hidden sm:inline">{tab.title}</span>
                <span className="sm:hidden">{tab.shortTitle || tab.title}</span>
              </label>
              {index < visibleTabs.length - 1 && (
                <div className="absolute right-0 top-0 h-full w-[1px] bg-zinc-300" />
              )}
            </li>
          ))}
        </ul>

        {visibleTabs.map((tab) => (
          <div key={tab.id} className="hidden">
            {tab.content}
          </div>
        ))}
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html: `
          ${visibleTabs
            .map((_, index) => {
              const tabIndex = index + 1;
              return `
              input[type="radio"]:nth-of-type(${tabIndex}):checked ~ ul li:nth-of-type(${tabIndex}) label {
                background: white !important;
              }
              
              input[type="radio"]:nth-of-type(${tabIndex}):checked ~ ul li:nth-of-type(${tabIndex})::after {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                height: 3px;
                background: #C41819;
                z-index: 10;
              }
              
              input[type="radio"]:nth-of-type(${tabIndex}):checked ~ div:nth-of-type(${tabIndex}) {
                display: block !important;
              }
            `;
            })
            .join('')}
        `,
        }}
      />
    </div>
  );
}
